import { FileUp, X } from 'lucide-react';
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { cn } from './cn.js';
import { formatFileSize } from './file-size.js';
import { IconButton } from './icon-button.js';

export interface FileDropzoneProps {
  label: string;
  files: File[];
  onFilesChange: (files: File[]) => void;
  /// Mesmo formato do `accept` do HTML: tipos (`image/png`), famílias (`image/*`) ou extensões.
  accept?: string | undefined;
  /// Ligado por padrão: escolher ou arrastar soma arquivos à lista, em vez de substituir.
  multiple?: boolean | undefined;
  maxSizeBytes?: number | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  chooseText?: string | undefined;
  dropText?: string | undefined;
  /// Rótulo só para leitor de tela, quando o título da janela ou da seção já diz o que é.
  hideLabel?: boolean | undefined;
  /// Outros caminhos de entrada ao lado de Escolher, como fotografar ou colar um print.
  actions?: ReactNode | undefined;
  /// Substitui o conteúdo padrão da caixa — a prévia de uma logo já enviada, por exemplo —
  /// mantendo a caixa como destino do arraste. Quem usa abre o seletor pela referência.
  children?: ReactNode | undefined;
  zoneClassName?: string | undefined;
  /// Nome da caixa como grupo, quando ela reúne vários caminhos de entrada.
  zoneLabel?: string | undefined;
  /// Nome do botão que tira um arquivo da lista; o padrão é "Remover <nome>".
  removeLabel?: ((file: File) => string) | undefined;
}

const sameFile = (a: File, b: File) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

function accepts(accept: string | undefined, file: File) {
  if (!accept) return true;
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  // Sem tipo informado pelo sistema do aparelho, só o servidor pode decidir; recusar aqui
  // barraria arquivo válido.
  if (!type && !accept.split(',').some((token) => token.trim().startsWith('.'))) return true;
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) =>
      token.startsWith('.')
        ? name.endsWith(token)
        : token.endsWith('/*')
          ? type.startsWith(token.slice(0, -1))
          : type === token,
    );
}

/// Envio de arquivos por arrastar e soltar ou pelo seletor do aparelho, no lugar do campo nativo.
///
/// O próprio campo de arquivo continua sendo o alvo do teclado e do leitor de tela — fica
/// invisível, e a caixa mostra o foco dele. Assim Enter ou Espaço abrem o seletor sem um segundo
/// controle, e o resumo de erros do formulário encontra o campo pelo rótulo. Arrastar não respeita
/// o `accept` do navegador; por isso o filtro de formato e tamanho é refeito aqui, para os dois
/// caminhos, e o que foi recusado aparece nomeado.
export const FileDropzone = forwardRef<HTMLInputElement, FileDropzoneProps>(function FileDropzone(
  {
    label,
    files,
    onFilesChange,
    accept,
    multiple = true,
    maxSizeBytes,
    hint,
    error,
    disabled = false,
    chooseText = multiple ? 'Escolher arquivos' : 'Escolher arquivo',
    dropText = multiple ? 'Arraste os arquivos para cá' : 'Arraste o arquivo para cá',
    hideLabel = false,
    actions,
    children,
    zoneClassName,
    zoneLabel,
    removeLabel = (file) => `Remover ${file.name}`,
  },
  ref,
) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!, []);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState<string>();
  const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  function add(incoming: File[]) {
    if (disabled || !incoming.length) return;
    const wrongType = incoming.filter((file) => !accepts(accept, file));
    const tooBig = incoming.filter(
      (file) => accepts(accept, file) && maxSizeBytes !== undefined && file.size > maxSizeBytes,
    );
    const valid = incoming.filter((file) => !wrongType.includes(file) && !tooBig.includes(file));
    const notes = [
      wrongType.length
        ? `Formato não aceito: ${wrongType.map((file) => file.name).join(', ')}.`
        : '',
      tooBig.length
        ? `Acima de ${formatFileSize(maxSizeBytes!)}: ${tooBig.map((file) => file.name).join(', ')}.`
        : '',
    ].filter(Boolean);
    setRejected(notes.length ? notes.join(' ') : undefined);
    if (!valid.length) return;
    if (!multiple) {
      onFilesChange([valid[0]!]);
      return;
    }
    const next = [...files];
    for (const file of valid) if (!next.some((kept) => sameFile(kept, file))) next.push(file);
    onFilesChange(next);
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    add(Array.from(event.dataTransfer.files));
  }

  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className={cn('block text-xs font-semibold text-content/70', hideLabel && 'sr-only')}
      >
        {label}
      </label>
      <input
        ref={inputRef}
        id={id}
        type="file"
        className="peer sr-only"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        onChange={(event) => {
          add(Array.from(event.target.files ?? []));
          // Limpar permite escolher de novo o mesmo arquivo depois de removê-lo da lista.
          event.target.value = '';
        }}
      />
      <div
        data-slot="file-dropzone"
        role={zoneLabel ? 'group' : undefined}
        aria-label={zoneLabel}
        data-dragging={dragging || undefined}
        onDragEnter={(event) => {
          event.preventDefault();
          if (disabled) return;
          depth.current += 1;
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          // Evento sintético sem transferência (teclado, testes) não tem o que sinalizar.
          if (event.dataTransfer) event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
        }}
        onDragLeave={() => {
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setDragging(false);
        }}
        onDrop={drop}
        className={cn(
          'rounded-xl border border-dashed px-4 py-6 text-center transition peer-focus-visible:border-brand-500 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/20',
          dragging
            ? 'border-brand-500/60 bg-brand-500/10'
            : error
              ? 'border-danger/70 bg-surface-elevated'
              : 'border-surface-border bg-surface-elevated',
          disabled && 'opacity-60',
          zoneClassName,
        )}
      >
        {children ?? (
          <>
            <FileUp className="mx-auto mb-2 size-5 text-brand-400" aria-hidden="true" />
            <p className="text-sm text-content [@media(pointer:coarse)]:hidden">{dropText}</p>
            <p className="mb-3 text-xs text-content-muted [@media(pointer:coarse)]:hidden">ou</p>
            <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
              {/* Segundo rótulo do mesmo campo: o clique abre o seletor sem script, e o nome
                  acessível continua começando pelo rótulo principal. */}
              <label
                htmlFor={id}
                aria-hidden="true"
                className={cn(
                  'inline-flex h-10 items-center justify-center rounded-xl border border-surface-border bg-surface px-4 text-sm font-semibold text-content transition hover:border-brand-500/60',
                  disabled ? 'pointer-events-none' : 'cursor-pointer',
                )}
              >
                {chooseText}
              </label>
              {actions}
            </div>
          </>
        )}
      </div>
      {files.length ? (
        <ul aria-label={`${label} escolhidos`} className="space-y-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="flex min-w-0 items-center gap-2 rounded-lg border border-surface-border px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 flex-1 truncate text-content">{file.name}</span>
              <span className="shrink-0 text-xs text-content-muted">
                {formatFileSize(file.size)}
              </span>
              <IconButton
                label={removeLabel(file)}
                icon={<X className="size-4" />}
                disabled={disabled}
                onClick={() => onFilesChange(files.filter((_, at) => at !== index))}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {rejected ? (
        <p role="status" className="text-xs leading-5 text-warning">
          {rejected}
        </p>
      ) : null}
      {error ? (
        <p id={descriptionId} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={descriptionId} className="text-xs leading-5 text-content-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
